import type { UI } from '@/v2/types';
import { Flex, Text } from '@qga/roo-ui/components';

import { CtaEvent } from '@shared/v2/components/createRoomOptionCTA/types';
import useBreakpoints, { isScreen } from '@shared/v2/hooks/useBreakpoints';
import useOtherRoomOptions from '@shared/v2/hooks/useOtherRoomOptions';
import scrollTo from '@shared/v2/utils/scrollIntoView';

import RoomList from '@/v2/components/pages/PackageView/components/RoomOptions/components/RoomList';
import { useOtherPackageModal } from '@/v2/components/pages/PackageView/contexts/OtherPackageModalContext';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '../../../../contexts/SearchPanelControlsContext';

export type OtherRoomsProps = {
  roomOptions: UI.RoomOption[] | undefined | null;
  roomIndexOffset?: number;
};

const OtherRooms = ({ roomOptions, roomIndexOffset }: OtherRoomsProps) => {
  const { route, isPreSearch } = useRoomOptionsData() || {};

  const { visibleRooms } = useOtherRoomOptions({
    roomOptions,
    route,
    isPreSearch,
  });

  const breakpoints = useBreakpoints();
  const isSmallScreen = isScreen(breakpoints)('xs', 'sm');

  const { openOtherPackageModal } = useOtherPackageModal();
  const { openMobileSearchPanelModal, openTravellers } =
    useSearchPanelControls();

  const handleCTAClick = (
    eventName: CtaEvent,
    data: { roomOption?: UI.RoomOption; ctaText?: string },
  ) => {
    if (eventName === 'CHANGE_TRAVELLERS') {
      if (isSmallScreen) {
        openMobileSearchPanelModal();
        openTravellers();
      } else {
        scrollTo('#travellers', { offset: -70, behavior: 'smooth' });
        openTravellers();
      }
    } else if (data.roomOption) {
      openOtherPackageModal(data.roomOption, data.ctaText);
    }
  };

  return visibleRooms?.length ? (
    <Flex alignItems="flex-start" flexDirection="column" mt="4">
      <Text fontSize="lg">Rooms in other package options</Text>
      <Text mb="6" color="greys.steel">
        These rooms are applicable to another package option
      </Text>
      <RoomList
        data-testid="OTHER_ROOMS"
        roomOptions={visibleRooms}
        roomIndexOffset={roomIndexOffset}
        onCtaClick={handleCTAClick}
      />
    </Flex>
  ) : null;
};

export default OtherRooms;
